import { bodyKindCode, type GameState } from './state.js';

/**
 * Hash FNV-1a 32-bit do estado, canônico e estável.
 * É a asserção central dos testes de replay: mesmos inputs → mesmo hash.
 * Se este número muda sem você querer, o comportamento do jogo mudou.
 */
const scratch = new ArrayBuffer(4);
const scratchF32 = new Float32Array(scratch);
const scratchU8 = new Uint8Array(scratch);

const FNV_PRIME = 0x01000193;
const FNV_OFFSET = 0x811c9dc5;

function mixByte(hash: number, byte: number): number {
  return Math.imul(hash ^ (byte & 0xff), FNV_PRIME) >>> 0;
}

function mixU32(hash: number, value: number): number {
  const v = value >>> 0;
  let h = hash;
  h = mixByte(h, v);
  h = mixByte(h, v >>> 8);
  h = mixByte(h, v >>> 16);
  h = mixByte(h, v >>> 24);
  return h;
}

/**
 * Hash de float via seus bits em f32.
 * Reduzir para f32 antes de hashear é intencional: é a mesma precisão que vai
 * no fio (campo f32 do protocolo), então o hash não distingue estados que
 * o cliente jamais poderia distinguir.
 */
function mixF32(hash: number, value: number): number {
  scratchF32[0] = value;
  let h = hash;
  for (let i = 0; i < 4; i++) h = mixByte(h, scratchU8[i] ?? 0);
  return h;
}

export function hashState(state: GameState): number {
  let h = FNV_OFFSET;
  h = mixU32(h, state.tick);
  h = mixU32(h, state.seed);
  h = mixU32(h, state.rngState);
  h = mixU32(h, state.players.length);
  for (const player of state.players) {
    h = mixU32(h, player.id);
    h = mixF32(h, player.x);
    h = mixF32(h, player.z);
    h = mixF32(h, player.vx);
    h = mixF32(h, player.vz);
    h = mixF32(h, player.ix);
    h = mixF32(h, player.iz);
    h = mixF32(h, player.facingX);
    h = mixF32(h, player.facingZ);
    h = mixU32(h, player.magnetTargetId);
  h = mixU32(h, player.magnetHeld ? 1 : 0);
    h = mixU32(h, player.hp);
    h = mixU32(h, player.lastSeq);
  }
  h = mixU32(h, state.bodies.length);
  for (const body of state.bodies) {
    h = mixU32(h, body.id);
    h = mixU32(h, bodyKindCode(body.kind));
    h = mixF32(h, body.x);
    h = mixF32(h, body.z);
    h = mixF32(h, body.vx);
    h = mixF32(h, body.vz);
    h = mixU32(h, body.heldBy);
  }
  return h >>> 0;
}

/** Hex de 8 dígitos, para mensagem de erro legível e fixture. */
export function hashStateHex(state: GameState): string {
  return hashState(state).toString(16).padStart(8, '0');
}
